<template>
    <section class="rulebook-feedback">
        <b-alert v-if="error" show variant="danger" class="d-flex align-items-center justify-content-between">
            <span>{{ error }}</span>
            <b-button size="sm" variant="outline-danger" @click="$emit('retry')">다시 불러오기</b-button>
        </b-alert>

        <div v-if="loading" class="text-center text-muted py-5">
            <b-spinner small class="mr-2" />피드백 현황을 불러오는 중입니다.
        </div>

        <template v-else>
            <b-alert show variant="light" class="model-note mb-4">
                <strong>평가자의 일은 기존과 같습니다.</strong>
                회사 답변 하나를 검토하고 AI 평가가 맞는지 피드백 한 번만 남깁니다.
                공통 원칙을 찾고 판단 기준을 시험하는 일은 시스템이 맡습니다.
            </b-alert>

            <b-row class="mb-4">
                <b-col v-for="metric in metrics" :key="metric.label" cols="12" sm="6" xl="3" class="mb-3">
                    <b-card class="metric-card h-100" body-class="p-3">
                        <div class="metric-label">{{ metric.label }}</div>
                        <div class="metric-value">{{ metric.value.toLocaleString() }}</div>
                        <small class="text-muted">{{ metric.description }}</small>
                    </b-card>
                </b-col>
            </b-row>

            <b-card class="mb-4" body-class="p-4">
                <h5 class="mb-3">한 건의 피드백은 이렇게 처리됩니다</h5>
                <div class="feedback-example">
                    <div class="example-block">
                        <span class="example-label">평가자가 남긴 피드백</span>
                        <p>“근로자 500명의 계약서를 모두 요구하지 말고 실제 계약서 한 건이나 표준양식도 증빙으로 인정해야 함”</p>
                    </div>
                    <div class="example-block result">
                        <span class="example-label">시스템이 정리한 공통 원칙</span>
                        <p>제도·절차 운영 여부를 묻는 문항에서는 ‘모든’이라는 표현을 전수 파일 제출 요구로 해석하지 않고 대표 문서와 표준양식을 인정한다.</p>
                    </div>
                </div>
            </b-card>

            <b-card body-class="p-4">
                <div class="d-flex flex-column flex-md-row justify-content-between mb-3">
                    <div>
                        <h5 class="mb-1">판단 기준 자료 준비 상태</h5>
                        <p class="text-muted mb-0">피드백 저장 원장까지 처리된 사례만 원칙 추출에 사용합니다.</p>
                    </div>
                    <b-badge :variant="overview.feedback.readyForRulebook > 0 ? 'success' : 'secondary'"
                             class="align-self-start mt-2 mt-md-0 ready-badge"
                    >
                        {{ overview.feedback.readyForRulebook.toLocaleString() }}건 준비
                    </b-badge>
                </div>
                <b-progress :max="Math.max(overview.feedback.total, 1)" height="12px">
                    <b-progress-bar :value="overview.feedback.readyForRulebook" variant="success" />
                </b-progress>
                <div class="d-flex justify-content-between small text-muted mt-2">
                    <span>준비됨 {{ overview.feedback.readyForRulebook.toLocaleString() }}건</span>
                    <span>원장 처리 대기 {{ overview.feedback.waitingForLedger.toLocaleString() }}건</span>
                </div>
            </b-card>
        </template>
    </section>
</template>

<script>
export default {
    name: 'RulebookFeedbackTab',
    props: {
        overview: {type: Object, required: true},
        loading: {type: Boolean, default: false},
        error: {type: String, default: ''},
    },
    computed: {
        metrics() {
            const feedback = this.overview.feedback;
            return [
                {label: '전체 피드백', value: feedback.total, description: '회사 답변별 최신 피드백'},
                {label: '그대로 인정', value: feedback.accepted, description: 'AI 평가가 맞았던 사례'},
                {label: '평가 수정', value: feedback.corrected, description: '새 원칙의 주요 재료'},
                {label: '사용 불가', value: feedback.rejected, description: '정답을 확정할 수 없는 사례'},
            ];
        },
    },
};
</script>

<style scoped lang="scss">
.model-note {
    border: 1px solid #d9e4ef;
    color: #354052;
}

.metric-card {
    border-color: #e0e5eb;
}

.metric-label,
.example-label {
    color: #687485;
    font-size: 0.78rem;
    font-weight: 700;
}

.metric-value {
    color: #233142;
    font-size: 1.7rem;
    font-weight: 700;
    line-height: 1.2;
    margin: 0.35rem 0;
}

.feedback-example {
    display: grid;
    gap: 1rem;
    grid-template-columns: repeat(2, minmax(0, 1fr));
}

.example-block {
    background: #f8f9fa;
    border: 1px solid #e1e6eb;
    border-radius: 6px;
    padding: 1rem;

    &.result {
        background: #eef7f1;
        border-color: #cce4d3;
    }

    p {
        color: #354052;
        font-size: 0.9rem;
        line-height: 1.65;
        margin: 0.5rem 0 0;
    }
}

.ready-badge {
    font-size: 0.82rem;
    padding: 0.55rem 0.7rem;
}

@media (max-width: 767px) {
    .feedback-example {
        grid-template-columns: 1fr;
    }
}
</style>
